import Vue from 'vue'
import store from './store'
import Router from 'vue-router'
import Home from './views/Home.vue'
import Eula from './views/Eula.vue'
import { isSafeAppRedirect } from './mobile/standaloneLaunch'

Vue.use(Router)

const router = new Router({
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home,
    },
    {
      path: '/eula',
      name: 'eula',
      component: Eula,
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('./views/Register.vue'),
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('./views/Profile.vue'),
    },
    {
      path: '/user/:userId',
      name: 'userProfile',
      component: () => import('./views/Profile.vue'),
    },
    {
      path: '/landing',
      name: 'landing',
      component: () => import('./views/Landing.vue'),
    },
    {
      // Manage Gold (MG-12). Own route — not a Landing tab. E1: Gold Editor only.
      path: '/company-console',
      name: 'companyConsole',
      component: () => import('./views/CompanyConsole.vue'),
      meta: { requiresGoldEditor: true },
    },
    {
      // GTM Maestro (EW-0). Own route — not a Landing tab. GTM Admin flag only.
      path: '/gtm',
      name: 'gtmMaestro',
      component: () => import('./views/GtmMaestro.vue'),
      meta: { requiresGtmAdmin: true },
    },
    {
      // TUBA BLAST (EW-T). Org-wide standing mesh — not Point Partners.
      path: '/tuba',
      name: 'tubaBlast',
      component: () => import('./views/TubaBlast.vue'),
      meta: { requiresTubaBlast: true },
    },
    {
      path: '/add-customers',
      name: 'addCustomers',
      component: () => import('./views/AddCustomers.vue'),
    },
    {
      path: '/add-partners',
      name: 'addPartners',
      component: () => import('./views/AddPartners.vue'),
    },
    {
      path: '/company/:companyId',
      name: 'company',
      component: () => import('./views/Company.vue'),
    },
    {
      path: '/logout',
      name: 'logout',
      // route level code-splitting
      // this generates a separate chunk (logout.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import('./views/Logout.vue'),
    },
    {
      path: '/change_password',
      name: 'changepassword',
      component: () => import('./views/ChangePassword.vue'),
    },
    {
      path: '/forgot',
      name: 'forgot',
      component: () => import('./views/ForgotPassword.vue'),
    },
    {
      path: '/reset',
      name: 'reset',
      component: () => import('./views/ResetPassword.vue'),
    },
    {
      path: '/kickoff',
      name: 'kickoff',
      component: () => import('./views/KickoffArrive.vue'),
    },
  ]
})

router.beforeEach(async (to, _, next) => {
  const authNotRequired = ['home', 'register', 'forgot', 'reset', 'kickoff']
  const eulaNotRequired = ['home', 'login', 'logout', 'eula', 'register', 'forgot', 'reset', 'kickoff']
  // Same priority as Login.vue: EULA → password reset → phone proof → employer.
  const passwordResetAllowed = ['changepassword', 'logout', 'eula', 'forgot', 'reset', 'kickoff']
  const phoneProofAllowed = ['kickoff', 'logout', 'eula', 'changepassword', 'forgot', 'reset']
  const employerAllowed = ['profile', 'userProfile', 'logout', 'changepassword', 'eula', 'forgot', 'reset', 'kickoff']

  // Restore from localStorage, then reconcile id against the token (/me) so a
  // stale Vuex id cannot blank Matrix/Manage until the next logout/login.
  await store.dispatch('persistLogin')
  const isLoggedIn = store.getters.isLoggedIn
  const hasAcceptedEula = store.getters.hasAcceptedEula
  const needsPasswordReset = store.getters.requiresPasswordReset
  const needsPhoneProof = store.getters.needsPhoneProof
  const needsEmployer = store.getters.needsEmployer
  if (!authNotRequired.includes(to.name) && !isLoggedIn) {
    // Rocket Launcher RL-5: keep intended destination for post-login return.
    const q = (to.name && to.name !== 'home')
      ? { redirect: to.fullPath || to.path }
      : undefined
    next(q ? { name: 'home', query: q } : { name: 'home' })
  } else if (!eulaNotRequired.includes(to.name) && isLoggedIn && !hasAcceptedEula) {
    // If they're logged in and have not accepted the eula, send them to the eula page
    next({ name: 'eula' })
  } else if (
    isLoggedIn &&
    hasAcceptedEula &&
    needsPasswordReset &&
    !passwordResetAllowed.includes(to.name)
  ) {
    next({ name: 'changepassword' })
  } else if (
    isLoggedIn &&
    hasAcceptedEula &&
    !needsPasswordReset &&
    needsPhoneProof &&
    !phoneProofAllowed.includes(to.name)
  ) {
    next({ name: 'kickoff' })
  } else if (
    isLoggedIn &&
    hasAcceptedEula &&
    !needsPasswordReset &&
    !needsPhoneProof &&
    needsEmployer &&
    !employerAllowed.includes(to.name)
  ) {
    next({ name: 'profile' })
  } else if (isLoggedIn && hasAcceptedEula && to.name === 'home') {
    // Logged-in on login URL → next gate (landing only when complete).
    if (needsPasswordReset) {
      next({ name: 'changepassword' })
    } else if (needsPhoneProof) {
      next({ name: 'kickoff' })
    } else if (needsEmployer) {
      next({ name: 'profile' })
    } else if (to.query && isSafeAppRedirect(to.query.redirect)) {
      // RL-5: honor return URL after session restore on Home Screen cold start
      next(to.query.redirect)
    } else {
      next({ name: 'landing' })
    }
  } else if (to.meta && to.meta.requiresGoldEditor && !store.getters.canEditGoldenRecord) {
    // MG-12 / E1: Gold Editor flag only — superuser alone is not enough.
    next({ name: 'landing' })
  } else if (to.meta && to.meta.requiresGtmAdmin && !store.getters.canOpenGtmMaestro) {
    // EW-0: GTM Admin flag only — superuser alone is not enough.
    next({ name: 'landing' })
  } else if (to.meta && to.meta.requiresTubaBlast && !store.getters.canTubaBlast) {
    // EW-T: TUBA BLAST flag only — not implied by Maestro or superuser.
    next({ name: 'landing' })
  } else {
    // otherwise send them on their way
    next()
  }
})

export default router;
